import { useState, useCallback, useEffect } from 'react';
import type { User } from '../types';

/**
 * Cookie-only auth hook (CF rebuild).
 *
 * The CF Worker drops a session cookie on login/register; the
 * client never holds a token. Source of truth for "am I signed in?"
 * is the server: we call /api/auth/me on mount and trust the cookie.
 *
 * We persist the *user descriptor* in localStorage only as a
 * convenience for downstream callers after the server confirms the
 * cookie. We do not render from that cache before /api/auth/me
 * succeeds because custom-domain cutovers can leave stale cached users
 * without a matching host-only session cookie.
 */

export function onAuthError() {
  // Cookie-only: just nuke the local cache and bounce to /.
  try {
    localStorage.removeItem('pp_user');
  } catch {
    /* ignore */
  }
  fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(
    () => {},
  );
  window.location.href = '/';
}

/** Fetch /api/auth/me and synthesize PP's local User shape. */
async function probeMe(): Promise<User | null> {
  try {
    const res = await fetch('/api/auth/me', { credentials: 'include' });
    if (!res.ok) return null;
    const data = (await res.json()) as {
      user: { id: string; display_name: string | null; email: string };
    };
    return {
      id: data.user.id,
      username: data.user.display_name || data.user.email,
      role: 'user',
      user_type: 'human',
    };
  } catch {
    return null;
  }
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  // Synthetic token kept for API compatibility; the cookie is what
  // actually authenticates requests.
  const [token] = useState<string>('cookie-auth');
  const [isLoading, setIsLoading] = useState(true);

  const doLogout = useCallback(() => {
    fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(
      () => {},
    );
    try {
      localStorage.removeItem('pp_user');
      localStorage.removeItem('pp_token'); // legacy cleanup
    } catch {
      /* ignore */
    }
    setUser(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const me = await probeMe();
      if (cancelled) return;
      setUser(me);
      try {
        if (me) localStorage.setItem('pp_user', JSON.stringify(me));
        else localStorage.removeItem('pp_user');
      } catch {
        /* ignore */
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handler = () => doLogout();
    window.addEventListener('pp:logout-requested', handler);
    return () => window.removeEventListener('pp:logout-requested', handler);
  }, [doLogout]);

  const handleLogin = useCallback(
    (loginData: {
      token: string;
      user_id: string;
      username: string;
      role: string;
    }) => {
      const u: User = {
        id: loginData.user_id,
        username: loginData.username,
        role: loginData.role,
        user_type: 'human',
      };
      try {
        localStorage.setItem('pp_user', JSON.stringify(u));
      } catch {
        /* ignore */
      }
      setUser(u);
    },
    [],
  );

  return { user, token, isLoading, handleLogin, logout: doLogout };
}
